/**
 * Phase 107 (Plan 107-07 / D-12, D-13, T-107-MONEYDRIFT) — exact,
 * dependency-free mirror of the backend's monthly-price derivation, for the
 * SLA Pricing editor's live preview over **unsaved** local paint state.
 *
 * Counterpart file: `backend/src/services/__tests__/slaPricingCostService.test.ts`.
 * If `priceMath.test.ts` and that file ever disagree, the client preview and
 * the saved quote disagree.
 *
 * Why not `Decimal` and why not floats. The backend is `Decimal`-safe.
 * `decimal.js` is a backend-only dependency at HEAD, and this phase adds no
 * new dependency. Plain float multiplication is also unacceptable: the
 * backend test's `'100.03'` fixture is precisely a case where float
 * arithmetic yields `21656.494999999999` and rounds to `'21656.49'` while the
 * exact answer is `'21656.50'`. So this module computes in **integer minor
 * units** instead, which is exact for this domain.
 *
 * This module is pure: it imports nothing, performs no I/O, and has no React
 * dependency. It exports no function that writes, mutates or persists
 * anything.
 */

/**
 * D-13's 4.33 monthly factor expressed exactly as an integer ratio —
 * `433 / 100` — rather than a decimal literal, so the arithmetic below never
 * touches floating point. The backend's `slaPricingCostService.ts` uses
 * `Decimal('4.33')`, the same factor; both are pinned to identical fixtures
 * (see `priceMath.test.ts`) so they cannot drift.
 */
export const WEEKS_PER_MONTH_NUMERATOR = 433;
export const WEEKS_PER_MONTH_DENOMINATOR = 100;

export interface ClientBandInput {
  id: number;
  name: string;
  color: string;
  abbreviation: string;
  hourlyRate: string | null;
}

export interface ClientBandBreakdown {
  bandId: number;
  name: string;
  color: string;
  abbreviation: string;
  hoursPerWeek: number;
  hourlyRate: string | null;
  monthlyAmount: string;
  hasRate: boolean;
}

/**
 * Parses a rate string like `'100.03'` into integer cents (`10003`) by
 * splitting on the dot and padding the fractional part to two digits —
 * never via a whole-string float coercion. `parseInt` operates on individual
 * digit runs only, never on the combined decimal value.
 */
function parseRateToCents(hourlyRate: string): number {
  const [wholePart, fractionalPart = ''] = hourlyRate.split('.');
  const cents = fractionalPart.padEnd(2, '0').slice(0, 2);
  const wholeUnits = parseInt(wholePart || '0', 10);
  const centUnits = parseInt(cents || '0', 10);
  return wholeUnits * 100 + centUnits;
}

/**
 * Renders integer cents as a fixed-2 decimal string with no grouping
 * separators, matching the backend's `.toFixed(2)` output byte for byte.
 */
export function formatMinorUnits(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  const whole = Math.floor(abs / 100);
  const remainder = abs % 100;
  return `${sign}${whole}.${String(remainder).padStart(2, '0')}`;
}

interface InternalBreakdownRow {
  bandId: number;
  name: string;
  color: string;
  abbreviation: string;
  hoursPerWeek: number;
  hourlyRate: string | null;
  monthlyCents: number;
  hasRate: boolean;
}

/**
 * Shared computation for both exported functions: per-band hours painted
 * this week, and — for bands with a rate — the monthly amount in integer
 * cents.
 *
 * Magnitude bound: the maximum realistic value is 99999.99 per hour over 168
 * hours across all bands, which is under 8e11 hundredth-cents — three orders
 * of magnitude below `Number.MAX_SAFE_INTEGER` (~9e15), so this integer
 * arithmetic never overflows.
 *
 * D-09: a band with a null `hourlyRate` still appears here — with its true
 * `hoursPerWeek` and `hasRate: false` — rather than being silently omitted
 * or zero-priced without explanation. A band with zero painted cells is
 * omitted entirely.
 *
 * D-12: sorted descending by monthly amount so the band driving the price is
 * always the first row; ties break by band id ascending for a stable order.
 */
function computeBreakdownInternal(
  bands: ClientBandInput[],
  cells: { bandId: number }[],
): InternalBreakdownRow[] {
  const hoursByBandId = new Map<number, number>();
  for (const cell of cells) {
    hoursByBandId.set(cell.bandId, (hoursByBandId.get(cell.bandId) ?? 0) + 1);
  }

  const rows: InternalBreakdownRow[] = [];
  for (const band of bands) {
    const hoursPerWeek = hoursByBandId.get(band.id) ?? 0;
    if (hoursPerWeek === 0) {
      continue;
    }

    const hasRate = band.hourlyRate !== null;
    let monthlyCents = 0;
    if (hasRate) {
      const rateCents = parseRateToCents(band.hourlyRate as string);
      const weeklyCents = rateCents * hoursPerWeek;
      const monthlyHundredthCents = weeklyCents * WEEKS_PER_MONTH_NUMERATOR;
      // Round half-up to cents.
      monthlyCents = Math.floor((monthlyHundredthCents + 50) / WEEKS_PER_MONTH_DENOMINATOR);
    }

    rows.push({
      bandId: band.id,
      name: band.name,
      color: band.color,
      abbreviation: band.abbreviation,
      hoursPerWeek,
      hourlyRate: band.hourlyRate,
      monthlyCents,
      hasRate,
    });
  }

  rows.sort((a, b) => {
    const diff = b.monthlyCents - a.monthlyCents;
    return diff !== 0 ? diff : a.bandId - b.bandId;
  });

  return rows;
}

export function computeBandBreakdownClient(
  bands: ClientBandInput[],
  cells: { bandId: number }[],
): ClientBandBreakdown[] {
  return computeBreakdownInternal(bands, cells).map((row) => ({
    bandId: row.bandId,
    name: row.name,
    color: row.color,
    abbreviation: row.abbreviation,
    hoursPerWeek: row.hoursPerWeek,
    hourlyRate: row.hourlyRate,
    monthlyAmount: formatMinorUnits(row.monthlyCents),
    hasRate: row.hasRate,
  }));
}

export function computeMonthlyPriceClient(
  bands: ClientBandInput[],
  cells: { bandId: number }[],
): string {
  const totalCents = computeBreakdownInternal(bands, cells).reduce(
    (sum, row) => sum + row.monthlyCents,
    0,
  );
  return formatMinorUnits(totalCents);
}
